import {
  BarChart3,
  Calendar,
  Camera,
  CheckSquare,
  Clock,
  ClipboardList,
  DollarSign,
  FileCheck,
  FileImage,
  FileText,
  FolderOpen,
  LayoutDashboard,
  PlusCircle,
  Receipt,
  Search,
  Settings,
  ShoppingCart,
  Truck,
  Users,
  AlertTriangle,
  X,
} from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { usePermissions } from '../../hooks/usePermissions';
import { projectService } from '../../services/projectService';

interface SidebarItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  permission?: string;
}

const ProjectSidebar: React.FC = () => {
  const projectId = useSelector((state: RootState) => state.project.projectId);
  const location = useLocation();
  const { isAuthenticated } = usePermissions(projectId || undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [projectName, setProjectName] = useState<string>('');
  const [isLoadingProject, setIsLoadingProject] = useState(false);

  // Fetch project details to get the name
  useEffect(() => {
    const fetchProjectName = async () => {
      if (!projectId) return;

      setIsLoadingProject(true);
      try {
        const response = await projectService.getProject(projectId);
        if (response.success && response.data) {
          setProjectName(response.data.name || '');
        }
      } catch (error) {
        console.error('Failed to fetch project name:', error);
      } finally {
        setIsLoadingProject(false);
      }
    };

    fetchProjectName();
  }, [projectId]);

  // Don't render if no project is selected or user is not authenticated
  if (!projectId || !isAuthenticated) {
    return null;
  }

  const sidebarItems: SidebarItem[] = [
    {
      key: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      route: `/projects/${projectId}/dashboard`,
    },
    {
      key: 'takeoff',
      label: 'Takeoff',
      icon: FileText,
      route: `/projects/${projectId}/takeoff`,
      permission: 'takeoff.view',
    },
    {
      key: 'estimates',
      label: 'Estimates',
      icon: DollarSign,
      route: `/projects/${projectId}/estimates`,
      permission: 'estimate.view',
    },
    {
      key: 'proposal',
      label: 'Proposal',
      icon: FileCheck,
      route: `/projects/${projectId}/proposal`,
      permission: 'proposal.view',
    },
    {
      key: 'schedule',
      label: 'Schedule',
      icon: Calendar,
      route: `/projects/${projectId}/schedule`,
      permission: 'schedule.view',
    },
    {
      key: 'bid-requests',
      label: 'Bid Requests',
      icon: ClipboardList,
      route: `/projects/${projectId}/bid-requests`,
      permission: 'bid_requests.view',
    },
    {
      key: 'selections',
      label: 'Selections and Allowances',
      icon: CheckSquare,
      route: `/projects/${projectId}/selections`,
      permission: 'selections.view',
    },
    {
      key: 'purchase-orders',
      label: 'PO and Subcontracts',
      icon: ShoppingCart,
      route: `/projects/${projectId}/purchase-orders`,
      permission: 'purchase_orders.view',
    },
    {
      key: 'bills',
      label: 'Bills',
      icon: Receipt,
      route: `/projects/${projectId}/bills`,
      permission: 'bills.view',
    },
    {
      key: 'change-orders',
      label: 'Change Orders',
      icon: PlusCircle,
      route: `/projects/${projectId}/change-orders`,
      permission: 'change_orders.view',
    },
    {
      key: 'budget',
      label: 'Budget',
      icon: BarChart3,
      route: `/projects/${projectId}/budget`,
      permission: 'budget.view',
    },
    {
      key: 'client-invoices',
      label: 'Client Invoices',
      icon: DollarSign,
      route: `/projects/${projectId}/client-invoices`,
      permission: 'client_invoices.view',
    },
    {
      key: 'daily-logs',
      label: 'Daily Logs',
      icon: ClipboardList,
      route: `/projects/${projectId}/daily-logs`,
      permission: 'daily_logs.view',
    },
    {
      key: 'timesheets',
      label: 'Timesheets',
      icon: Clock,
      route: `/projects/${projectId}/timesheets`,
      permission: 'timesheets.view',
    },
    {
      key: 'todos',
      label: 'To-Dos',
      icon: CheckSquare,
      route: `/projects/${projectId}/todos`,
      permission: 'todos.view',
    },
    {
      key: 'rfis',
      label: 'RFIs',
      icon: FileText,
      route: `/projects/${projectId}/rfis`,
      permission: 'rfis.view',
    },
    {
      key: 'files',
      label: 'Files',
      icon: FolderOpen,
      route: `/projects/${projectId}/files`,
      permission: 'files.view',
    },
    {
      key: 'specifications',
      label: 'Specifications',
      icon: FileText,
      route: `/projects/${projectId}/specifications`,
      permission: 'specifications.view',
    },
    {
      key: 'drawings',
      label: 'Drawings',
      icon: FileImage,
      route: `/projects/${projectId}/drawings`,
      permission: 'drawings.view',
    },
    {
      key: 'photos',
      label: 'Photos',
      icon: Camera,
      route: `/projects/${projectId}/photos`,
      permission: 'photos.view',
    },
    {
      key: 'deliveries',
      label: 'Deliveries',
      icon: Truck,
      route: `/projects/${projectId}/deliveries`,
      permission: 'deliveries.view',
    },
    {
      key: 'equipment',
      label: 'Equipment',
      icon: Settings,
      route: `/projects/${projectId}/equipment`,
      permission: 'equipment.view',
    },
    {
      key: 'incidents',
      label: 'Incident Reporting',
      icon: AlertTriangle,
      route: `/projects/${projectId}/incidents`,
      permission: 'incidents.view',
    },
    {
      key: 'quality',
      label: 'Quality Reports',
      icon: CheckSquare,
      route: `/projects/${projectId}/quality`,
      permission: 'quality.view',
    },
    {
      key: 'safety',
      label: 'Safety Reports',
      icon: AlertTriangle,
      route: `/projects/${projectId}/safety`,
      permission: 'safety.view',
    },
    {
      key: 'field-reports',
      label: 'Field Reports',
      icon: ClipboardList,
      route: `/projects/${projectId}/field-reports`,
      permission: 'field_reports.view',
    },
    {
      key: 'punch-list',
      label: 'Punch List',
      icon: CheckSquare,
      route: `/projects/${projectId}/punch-list`,
      permission: 'punch_list.view',
    },
    {
      key: 'milestones',
      label: 'Milestones',
      icon: Calendar,
      route: `/projects/${projectId}/milestones`,
      permission: 'milestones.view',
    },
    {
      key: 'compliance',
      label: 'Compliance Tracking',
      icon: FileCheck,
      route: `/projects/${projectId}/compliance`,
      permission: 'compliance.view',
    },
    {
      key: 'invoicing',
      label: 'Invoicing',
      icon: DollarSign,
      route: `/projects/${projectId}/invoicing`,
      permission: 'invoicing.view',
    },
    {
      key: 'team',
      label: 'Team',
      icon: Users,
      route: `/projects/${projectId}/team`,
      permission: 'team.view',
    },
  ];

  // All authenticated users see all modules
  // Frontend is permissive - backend enforces real authorization on API calls
  const visibleItems = sidebarItems;

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return visibleItems;
    const query = searchQuery.toLowerCase();
    return visibleItems.filter(
      item => item.label.toLowerCase().includes(query) || item.key.toLowerCase().includes(query)
    );
  }, [visibleItems, searchQuery]);

  const isActive = (route: string) => {
    return location.pathname === route;
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div
      className="w-64 bg-white border-r border-gray-200 flex flex-col h-full"
      data-testid="project-sidebar"
    >
      {/* Sidebar Header - Project Name */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <FolderOpen className="h-5 w-5 text-brand-primary" />
          </div>
          <div className="flex-1 min-w-0">
            {isLoadingProject ? (
              <div className="h-5 bg-gray-200 rounded animate-pulse w-32"></div>
            ) : (
              <h2 className="text-sm font-semibold text-gray-900 truncate" title={projectName}>
                {projectName || 'Project'}
              </h2>
            )}
            <p className="text-xs text-gray-500">{visibleItems.length} modules</p>
          </div>
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search modules..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 placeholder-gray-400"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {filteredItems.length === 0 ? (
          <div className="text-center py-8">
            <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No modules found</p>
            <button
              onClick={handleClearSearch}
              className="mt-2 text-sm text-brand-primary hover:text-brand-700"
            >
              Clear search
            </button>
          </div>
        ) : (
          <ul className="space-y-1">
            {filteredItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item.route);

              return (
                <li key={item.key}>
                  <Link
                    to={item.route}
                    className={`
                    group flex items-center w-full px-3 py-2 text-body-sm font-medium rounded-lg transition-all duration-200
                    ${
                      active
                        ? 'bg-brand-50 text-brand-primary border-l-4 border-brand-primary ml-0 pl-2'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-brand-primary'
                    }
                  `}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon
                      className={`
                      h-5 w-5 mr-3 transition-colors
                      ${active ? 'text-brand-primary' : 'text-gray-400 group-hover:text-brand-primary'}
                    `}
                    />
                    <span className="truncate">{item.label}</span>

                    {/* Active indicator */}
                    {active && (
                      <div className="ml-auto">
                        <div className="w-2 h-2 bg-brand-primary rounded-full"></div>
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {/* Sidebar Footer - Quick Stats */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-500">
          {searchQuery ? (
            <span>
              Showing {filteredItems.length} of {visibleItems.length} modules
            </span>
          ) : (
            <span>{visibleItems.length} modules available</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectSidebar;
